<template>
  <div class="constitution-analysis">
    <div class="card-container">
      <div class="card-header-center">
        <img src="https://pro-yeahgo-oss.yeahgo.com/file/zywzimg/体质解析.png" alt="体质解析" class="header-icon" />
        <span class="header-title">体质解析</span>
      </div>
      
      <div class="intro-text">
        多数人的体质特征是不典型，兼夹体质者为多，调理时建议以主体质为主，兼夹为辅
      </div>

      <!-- 雷达图区域 -->
      <div class="radar-chart-container">
        <div ref="radarChart" class="radar-chart"></div>
      </div>

      <div class="main-constitution" v-if="mainConstitution">
        您的主体质：<span class="highlight-text">{{ mainConstitution }}</span>
      </div>

      <div class="analysis-section-block" v-if="mainAnalysisItems.length">
        <div class="analysis-list">
          <div
            class="analysis-item"
            v-for="item in mainAnalysisItems"
            :key="item.key"
          >
            <div class="item-title main-title">
              <span class="constitution-name highlight-text">{{ mainConstitution }}</span>{{ item.label }}：
            </div>
            <div class="item-desc">{{ item.value }}</div>
          </div>
        </div>
      </div>

      <div
        class="sub-constitution-section"
        v-if="subConstitution && subAnalysisItems.length"
      >
        <div class="section-divider"></div>
        <div class="sub-constitution-header">
          兼有体质：<span class="highlight-text">{{ subConstitution }}</span>
        </div>
        <div class="analysis-list sub-analysis-list">
          <div
            class="analysis-item"
            v-for="item in subAnalysisItems"
            :key="item.key"
          >
            <div class="item-title sub-title">
              <span class="constitution-name sub-highlight-text">{{ subConstitution }}</span>{{ item.label }}：
            </div>
            <div class="item-desc">{{ item.value }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts';

export default {
  name: 'ConstitutionAnalysis',
  props: {
    reportData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      chartInstance: null
    };
  },
  computed: {
    constData() {
      return this.reportData?.tongueReport?.constitution || null;
    },
    mainConstitution() {
      return this.reportData?.constitution || '';
    },
    subConstitution() {
      return this.reportData?.constitution1 || '';
    },
    constitutionItems() {
      const rawList = this.constData?.exampleConstitution;
      if (Array.isArray(rawList) && rawList.length) {
        return rawList.map((item) => {
          if (typeof item === 'string') {
            return {
              name: item,
              val: 0,
              isCurr: 0
            };
          }

          return {
            name: item?.name || '',
            val: Math.max(0, Math.min(100, Number(item?.val || 0))),
            isCurr: Number(item?.isCurr || 0)
          };
        }).filter(item => item.name);
      }

      return [
        { name: '平和质', val: 0, isCurr: 0 },
        { name: '气虚质', val: 0, isCurr: 0 },
        { name: '阳虚质', val: 0, isCurr: 0 },
        { name: '阴虚质', val: 0, isCurr: 0 },
        { name: '痰湿质', val: 0, isCurr: 0 },
        { name: '湿热质', val: 0, isCurr: 0 },
        { name: '血瘀质', val: 0, isCurr: 0 },
        { name: '气郁质', val: 0, isCurr: 0 },
        { name: '特禀质', val: 0, isCurr: 0 }
      ];
    },
    mainAnalysisItems() {
      if (!this.constData || !this.mainConstitution) return [];

      return [
        { key: 'formingCause', label: '体质形成原因', value: this.constData.formingCause },
        { key: 'personalityTraits', label: '体质性格表现', value: this.constData.personalityTraits },
        { key: 'overallCharacteristics', label: '体质总体特征', value: this.constData.overallCharacteristics },
        { key: 'pulseCharacteristics', label: '体质脉象特征', value: this.constData.pulseCharacteristics },
        { key: 'tongueCharacteristics', label: '体质舌象特征', value: this.constData.tongueCharacteristics },
        { key: 'regulationPrinciple', label: '体质调理思路', value: this.constData.regulationPrinciple },
        { key: 'commonManifestations', label: '体质常见表现', value: this.constData.commonManifestations }
      ].filter(item => item.value);
    },
    subAnalysisItems() {
      if (!this.constData || !this.subConstitution) return [];

      return [
        {
          key: 'formingCauseOfConcurrentConstitution',
          label: '兼有体质形成原因',
          value: this.constData.formingCauseOfConcurrentConstitution
        }
      ].filter(item => item.value);
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initChart();
    });
  },
  watch: {
    reportData: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.initChart();
        });
      }
    }
  },
  methods: {
    initChart() {
      if (!this.$refs.radarChart) return;
      
      if (!this.chartInstance) {
        this.chartInstance = echarts.init(this.$refs.radarChart);
      }

      const indicators = this.constitutionItems.map(item => {
        const isHighlight = item.val >= 50;
        return {
          name: item.name,
          max: 100,
          min: 0,
          color: isHighlight ? '#44C2C4' : '#333'
        };
      });

      const dataValues = this.constitutionItems.map(item => item.val);

      const option = {
        tooltip: {},
        radar: {
          indicator: indicators,
          radius: '65%',
          splitNumber: 4,
          shape: 'polygon',
          axisName: {
            formatter: (value, indicator) => {
              return indicator.color === '#44C2C4'
                ? `{highlight|${value}}`
                : `{default|${value}}`;
            },
            rich: {
              highlight: {
                fontSize: 12,
                color: '#44C2C4'
              },
              default: {
                fontSize: 12,
                color: '#333'
              }
            }
          },
          splitArea: {
            areaStyle: {
              color: ['#E6F7F8', '#CFF0F1', '#B8E9EA', '#A1E2E3'].reverse(),
              shadowColor: 'rgba(0, 0, 0, 0.1)',
              shadowBlur: 10
            }
          },
          axisLine: {
            lineStyle: {
              color: '#8ACCCC'
            }
          },
          splitLine: {
            lineStyle: {
              color: '#8ACCCC'
            }
          }
        },
        series: [
          {
            name: '体质雷达',
            type: 'radar',
            data: [
              {
                value: dataValues,
                name: '体质',
                itemStyle: {
                  color: '#44C2C4'
                },
                areaStyle: {
                  color: 'rgba(68, 194, 196, 0.4)'
                },
                lineStyle: {
                  color: '#44C2C4',
                  width: 2
                },
                symbolSize: 4
              }
            ]
          }
        ]
      };

      this.chartInstance.setOption(option);
    }
  }
}
</script>

<style lang="scss" scoped>
.card-container {
  background: #fff;
  border-radius: 12px;
  padding: 22px 18px 20px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  margin-bottom: 16px;
}
.card-header-center {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}
.header-icon {
  width: 30px;
  height: 30px;
  margin-right: 8px;
  object-fit: contain;
}
.header-title {
  font-size: 18px;
  font-weight: bold;
  color: #333;
}

.intro-text {
  font-size: 12px;
  color: #7a7a7a;
  line-height: 1.75;
  text-align: justify;
  margin-bottom: 18px;
}

.radar-chart-container {
  width: 100%;
  height: 280px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 18px;
}

.radar-chart {
  width: 100%;
  height: 100%;
}

.main-constitution {
  text-align: center;
  font-size: 20px;
  font-weight: 600;
  color: #333;
  line-height: 1.5;
  margin-bottom: 34px;
}

.highlight-text {
  color: #44C2C4;
  font-weight: 700;
}

.sub-highlight-text {
  color: #D0A77A;
  font-weight: 700;
}

.analysis-section-block {
  margin-top: 4px;
}

.analysis-list {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.analysis-item {
  display: flex;
  flex-direction: column;
}

.item-title {
  font-size: 13px;
  font-weight: 400;
  color: #333;
  line-height: 1.6;
  margin-bottom: 6px;
}

.main-title {
  color: #333;
}

.sub-title {
  color: #333;
}

.item-title .highlight-text,
.item-title .sub-highlight-text,
.sub-constitution-header .highlight-text,
.sub-constitution-header .sub-highlight-text {
  color: #333;
}

.constitution-name {
  margin-right: 2px;
}

.item-desc {
  font-size: 12px;
  font-weight: 400;
  color: #333;
  line-height: 1.7;
  text-align: justify;
}

.sub-constitution-section {
  margin-top: 30px;
}

.section-divider {
  height: 1px;
  background: #EFEFEF;
  margin-bottom: 24px;
}

.sub-constitution-header {
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  line-height: 1.5;
  margin-bottom: 28px;
}

.sub-analysis-list {
  padding-bottom: 4px;
}
</style>